import React, { useState, useEffect, useCallback } from "react";
import env from "@beam-australia/react-env";

import useApi from "../../../../hooks/useApi";

import { useTranslation } from "react-i18next";
import useSkeleton from "../../../../hooks/useSkeleton";

import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { ProgressBar } from "primereact/progressbar";
import { Dialog } from "primereact/dialog";
import { classNames } from "primereact/utils";
import { Divider } from "primereact/divider";

import helperFormats from "../../../../utils/helperFormats";

import "react-toastify/dist/ReactToastify.css";
import WithholdingsLines from "./withholdingsLines";
import WithholdingsBillingDate from "./withholdingsBillingDate";

import { atom, useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";

const WithholdingsForm = ({ settings }) => {
  const [isLoading, setIsLoading] = useAtom(loadingState);

  const api = useApi();
  const s = useSkeleton();
  const { t } = useTranslation();

  const [withholding, setWithholding] = useState({});
  const [dataReady, setDataReady] = useState(false);

  const [confirmProgress, setConfirmProgress] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // Beneficiario cuya línea se está procesando en este momento, en qué paso va, y el error si el
  // proceso frenó.
  const [currentLineName, setCurrentLineName] = useState("");
  const [currentPhase, setCurrentPhase] = useState(null);
  const [confirmFailure, setConfirmFailure] = useState(null);
  // Avance de la fase en curso, y las líneas que quedaron sin procesar al terminar.
  const [phaseTotal, setPhaseTotal] = useState(0);
  const [phaseDone, setPhaseDone] = useState(0);
  const [confirmFailures, setConfirmFailures] = useState([]);

  const [showBillingDialog, setShowBillingDialog] = useState(false);

  const PHASE_COLLECTING = "COLLECTING";
  const PHASE_BILLING = "BILLING";
  // Se incrementa después de cada línea para que la grilla mueva la fila a "facturadas".
  const [linesRefreshToken, setLinesRefreshToken] = useState(0);

  const WITHHOLDING_STATE_CONCILATED = "WITHHOLDING_STATE_CONCILATED";
  const WITHHOLDING_STATE_CONFIRMED = "WITHHOLDING_STATE_CONFIRMED";
  const WITHHOLDING_STATE_SENT = "WITHHOLDING_STATE_SENT";
  const WITHHOLDING_STATE_CREATED = "WITHHOLDING_STATE_CREATED";

  const [withholdingState, setWithholdingState] = useState(
    "WITHHOLDING_STATE_CREATED"
  );

  const fetchData = useCallback(async () => {
    try {
      return await settings.CRUDService.getEntity(settings.entityId);
    } catch (error) {
      console.error(t("forms.messages.operation.error"), error);
      return null;
    }
  }, [settings.entityId, settings.formMode, settings.CRUDService]);

  const loadWithholding = useCallback(async () => {
    // El overlay de carga sólo va en la primera carga. En las recargas posteriores desmontaría la
    // grilla de líneas y, si el servidor no responde, volvería a montarla vacía: la pantalla
    // terminaba mostrando "0 líneas" cuando en realidad no se pudo leer nada.
    const esPrimeraCarga = !withholding?.id;

    if (esPrimeraCarga) {
      setDataReady(false);
    }

    const data = await fetchData();

    if (!data) {
      setDataReady(true);

      // Si ya había datos en pantalla se conservan: es más honesto que vaciar todo.
      if (!esPrimeraCarga) {
        settings.toast.current.show({
          severity: "warn",
          summary: t("forms.messages.titles.error"),
          detail: t("entities.withholdings.reloadFailed"),
          life: 6000,
        });
      }

      return;
    }

    setWithholding(data);

    const currentState = helperFormats.isDate(data.conciliationDate)
      ? WITHHOLDING_STATE_CONCILATED
      : helperFormats.isDate(data.confirmedDate)
        ? WITHHOLDING_STATE_CONFIRMED
        : helperFormats.isDate(data.sentDate)
          ? WITHHOLDING_STATE_SENT
          : WITHHOLDING_STATE_CREATED;

    setWithholdingState(currentState);

    // El avance se lee desde que la retención está enviada. La cabecera se cierra en cuanto está
    // toda la plata cobrada, así que "confirmada" no quiere decir "terminada": puede tener
    // comprobantes sin emitir, y hay que poder verlos y emitirlos. Lo mismo conciliada, porque
    // conciliar no exige estar facturado.
    if (currentState !== WITHHOLDING_STATE_CREATED) {
      try {
        const progress = await api.get(
          `${env("API_URL")}/withholdings/confirmationProgress/${data.id}`
        );

        setConfirmProgress(progress.data);
      } catch (error) {
        console.error(t("forms.messages.operation.error"), error);
      }
    }

    setDataReady(true);
    // withholding?.id se lee sólo para saber si es la primera carga; no hace falta reaccionar a él.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  /// Nombre del beneficiario de una línea, para poder decir a quién le está tocando el turno
  /// antes de que el servidor conteste.
  const lineBeneficiaryName = (lineId) => {
    const line = (withholding.withholdingsLinesCollection ?? []).find(
      (item) => item.id === lineId
    );

    return line
      ? `${line.beneficiary_Name ?? ""} ${line.beneficiary_LastName ?? ""}`.trim()
      : "";
  };

  // La fecha se manda como yyyy-MM-dd, armada con las partes locales. Con toISOString() el día se
  // corre según la zona horaria, y acá el día es justamente el dato.
  const toApiDate = (date) => {
    const mes = `${date.getMonth() + 1}`.padStart(2, "0");
    const dia = `${date.getDate()}`.padStart(2, "0");

    return `${date.getFullYear()}-${mes}-${dia}`;
  };

  // Anota el fallo de una línea en la lista de la corrida. Devuelve null cuando hay que abandonar
  // la corrida entera (el servidor dejó de responder: seguir pidiendo no tiene sentido).
  const registrarFallo = (fallos, lineId, error, esFacturacion) => {
    if (!error?.response) {
      return null;
    }

    const detalle = error.response.data?.failure;

    fallos.push({
      lineId: lineId,
      beneficiaryName: detalle?.beneficiaryName || lineBeneficiaryName(lineId),
      reason: detalle?.reason ?? error.message,
      paymentConfirmed: detalle?.paymentConfirmed ?? esFacturacion,
      billingStatus: detalle?.billingStatus,
      billingPhase: esFacturacion,
    });

    return fallos[fallos.length - 1];
  };

  // Recorre líneas llamando un endpoint por cada una. Ningún fallo corta el recorrido: frenar
  // dejaría sin procesar a las líneas siguientes, que no tienen nada que ver con la que falló.
  const recorrerLineas = async (lineIds, endpoint, esFacturacion, fallos) => {
    setPhaseTotal(lineIds.length);
    setPhaseDone(0);

    for (let i = 0; i < lineIds.length; i++) {
      const lineId = lineIds[i];

      setCurrentLineName(lineBeneficiaryName(lineId));

      try {
        const response = await api.put(
          `${env("API_URL")}/withholdings/confirmLine/${lineId}/${endpoint}`
        );

        setConfirmProgress(response.data);
      } catch (error) {
        if (!registrarFallo(fallos, lineId, error, esFacturacion)) {
          throw error;
        }
      }

      // Para que la grilla mueva la fila, tanto si la línea salió bien como si falló.
      setLinesRefreshToken((token) => token + 1);
      setPhaseDone(i + 1);
    }
  };

  // Cierre común de las dos corridas: refresca los contadores e informa lo que quedó.
  // currentPhase NO se limpia: el diálogo sigue abierto mostrando el resumen y tiene que saber cuál
  // de los dos procesos fue el que corrió.
  const terminarCorrida = async (fallos, mensajeDeFallos) => {
    setCurrentLineName("");

    const finalProgress = await api.get(
      `${env("API_URL")}/withholdings/confirmationProgress/${withholding.id}`
    );

    setConfirmProgress(finalProgress.data);
    setConfirmFailures(fallos);

    settings.toast.current.show({
      severity: fallos.length === 0 ? "success" : "warn",
      summary:
        fallos.length === 0
          ? t("forms.messages.titles.success")
          : t("forms.messages.titles.error"),
      detail:
        fallos.length === 0
          ? t("forms.messages.operation.success")
          : t(mensajeDeFallos, { count: fallos.length }),
      life: fallos.length === 0 ? 3000 : 8000,
    });

    await loadWithholding();
  };

  // Se llega acá sólo si el servidor dejó de responder: sin él no se puede seguir ni saber en qué
  // quedó cada línea. Lo hecho hasta el corte está firme.
  const cortarCorridaPorServidor = (error, fallos) => {
    setConfirmFailures(fallos);
    setConfirmFailure({
      beneficiaryName: currentLineName,
      reason: error?.response
        ? error.message
        : t("entities.withholdings.confirmProgressNoServer"),
    });

    settings.toast.current.show({
      severity: "error",
      summary: t("forms.messages.titles.error"),
      detail: t("forms.messages.operation.error"),
      life: 8000,
    });
  };

  const iniciarCorrida = (fase) => {
    // A propósito NO se usa el loading global: bloquea la pantalla completa y no informa nada.
    // El avance se muestra en el diálogo de progreso, que además impide tocar el resto.
    setIsConfirming(true);
    setConfirmFailure(null);
    setConfirmFailures([]);
    setCurrentLineName("");
    setCurrentPhase(fase);
    setPhaseTotal(0);
    setPhaseDone(0);
  };

  const confirmWithholding = async () => {
    iniciarCorrida(PHASE_COLLECTING);

    const fallos = [];

    try {
      const initialProgress = await api.get(
        `${env("API_URL")}/withholdings/confirmationProgress/${withholding.id}`
      );

      setConfirmProgress(initialProgress.data);

      // Se cobran todas las líneas pendientes. Facturar es un proceso aparte, que se corre después
      // con su propio botón y su propia fecha: confirmar no emite ningún comprobante.
      await recorrerLineas(
        initialProgress.data.pendingPaymentLineIds ?? [],
        "payment",
        false,
        fallos
      );

      await terminarCorrida(
        fallos,
        "entities.withholdings.confirmSummaryWithFailures"
      );
    } catch (error) {
      cortarCorridaPorServidor(error, fallos);
    } finally {
      setIsConfirming(false);
    }
  };

  // Factura la retención entera con la fecha elegida. Primero se le fija la fecha a la cabecera
  // (es lo que hace que los reintentos por línea usen la misma), y después se recorren las líneas.
  const billWithholding = async (billingDate) => {
    setShowBillingDialog(false);
    iniciarCorrida(PHASE_BILLING);

    const fallos = [];

    try {
      const started = await api.put(
        `${env("API_URL")}/withholdings/startBilling/${withholding.id}`,
        { billingDate: toApiDate(billingDate) }
      );

      setConfirmProgress(started.data);

      await recorrerLineas(
        started.data.pendingBillingLineIds ?? [],
        "billing",
        true,
        fallos
      );

      await terminarCorrida(
        fallos,
        "entities.withholdings.billSummaryWithFailures"
      );
    } catch (error) {
      // Si lo que falló fue fijar la fecha, no llegó a procesarse ninguna línea: se avisa el motivo
      // en vez de mostrar el corte genérico, que no diría nada útil.
      const motivo = billingStartError(error);

      if (motivo) {
        setIsConfirming(false);
        setCurrentPhase(null);

        settings.toast.current.show({
          severity: "warn",
          summary: t("forms.messages.titles.error"),
          detail: motivo,
          life: 8000,
        });

        await loadWithholding();

        return;
      }

      cortarCorridaPorServidor(error, fallos);
    } finally {
      setIsConfirming(false);
    }
  };

  // Traduce el rechazo de startBilling a un motivo en claro, o null si no fue eso lo que falló.
  const billingStartError = (error) => {
    if (error?.response?.status !== 422) {
      return null;
    }

    const data = error.response.data;
    const code = typeof data === "string" ? data : data?.message;

    if (code === "WITHHOLDING_BILLING_DATE_FROZEN") {
      return t("entities.withholdings.billingDateFrozenError", {
        date: helperFormats.formatDate(new Date(data.frozenDate)),
      });
    }

    if (code === "WITHHOLDING_BILLING_DATE_OUT_OF_RANGE") {
      return t("entities.withholdings.billingDateOutOfRange");
    }

    if (code === "WITHHOLDING_NOT_CONFIRMED") {
      return t("entities.withholdings.billingNotConfirmed");
    }

    return null;
  };

  const closeConfirmDialog = async () => {
    setConfirmFailure(null);
    setConfirmFailures([]);
    await loadWithholding();
  };

  // Cargar datos cuando cambia el entityId o formMode
  useEffect(() => {
    loadWithholding();
  }, [loadWithholding]);

  const updateWithholdings = async (action, id) => {
    setIsLoading(true);

    try {
      const response = await api.put(
        `${env("API_URL")}/withholdings/${action}/${id}`
      );

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });

      // Recargar datos después de la acción
      await loadWithholding();
    } catch (error) {
      // Un 422 trae el motivo en claro: se dice cuál es, en vez del error genérico.
      const rechazo =
        error?.response?.status === 422 && error.response.data === "WITHHOLDING_NOT_BILLED"
          ? t("entities.withholdings.conciliateNotBilled")
          : null;

      settings.toast.current.show({
        severity: rechazo ? "warn" : "error",
        summary: t("forms.messages.titles.error"),
        detail: rechazo ?? t("forms.messages.operation.error"),
        life: rechazo ? 8000 : 3000,
      });
      throw new Error();
    } finally {
      setIsLoading(false);
    }
  };

  // Una barra por proceso: cobrar y facturar corren por separado y nunca a la vez.
  const collectingPercentage =
    confirmProgress?.totalLines > 0
      ? Math.round(((confirmProgress.confirmedLines ?? 0) / confirmProgress.totalLines) * 100)
      : 0;

  const billingPercentage =
    confirmProgress?.totalLines > 0
      ? Math.round(((confirmProgress.billedLines ?? 0) / confirmProgress.totalLines) * 100)
      : 0;

  // Líneas que todavía esperan comprobante y que el reintento sí va a tomar. Las de verificación
  // manual no están acá: ésas las resuelve una persona desde la grilla.
  const pendingBillingCount = confirmProgress?.pendingBillingLineIds?.length ?? 0;
  const pendingPaymentCount = confirmProgress?.pendingPaymentLineIds?.length ?? 0;

  const canRunConfirmation = withholdingState === WITHHOLDING_STATE_SENT;

  // Facturar es un proceso posterior al cobro: recién con toda la plata firme (o sea, con la
  // retención confirmada) se emiten los comprobantes.
  //
  // Conciliada también cuenta, aunque hoy conciliar exija estar facturado: las retenciones que se
  // conciliaron antes de esa regla pueden tener comprobantes sin emitir, y si no fuera por acá no
  // tendrían ninguna forma de salir desde la pantalla.
  const canBill =
    (withholdingState === WITHHOLDING_STATE_CONFIRMED ||
      withholdingState === WITHHOLDING_STATE_CONCILATED) &&
    pendingBillingCount > 0;

  // La facturación está terminada cuando ninguna línea espera comprobante. Las de verificación
  // manual cuentan como pendientes: hasta que una persona no dice si el comprobante salió, no se
  // sabe si está facturada. Es la misma regla que aplica el servidor al conciliar.
  const billingComplete =
    (confirmProgress?.totalLines ?? 0) > 0 &&
    confirmProgress.billedLines === confirmProgress.totalLines;

  const canConciliate =
    withholdingState === WITHHOLDING_STATE_CONFIRMED && billingComplete;

  const showConfirmationSummary =
    (confirmProgress?.totalLines ?? 0) > 0 &&
    withholdingState !== WITHHOLDING_STATE_CREATED;

  if (!dataReady) {
    return (
      <div className="wh-loading-overlay">
        <ProgressSpinner style={{ width: '40px', height: '40px' }} />
        <span>Cargando...</span>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={s.get()}>
        <div className={s.get("withholdings.header")}>
          <div className={s.get("withholdings.header.summary")}>
            <div className={s.get("withholdings.header.summary.title")}>
              {t("entities.withholdings.generated")}
            </div>
            <div className={s.get("withholdings.header.summary.rows")}>
              <div className={s.get("withholdings.header.summary.rows.row")}>
                <div className={s.get("withholdings.header.field")}>
                  <label className={s.get("withholdings.header.field.label")}>
                    {t("entities.withholdings.year")}
                  </label>
                  <div className={s.get("withholdings.header.field.control")}>
                    <InputText
                      id="year"
                      value={withholding.year ?? ""}
                      className="flex w-full text-right"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className={s.get("withholdings.header.summary.rows.row")}>
                <div className={s.get("withholdings.header.field")}>
                  <label className={s.get("withholdings.header.field.label")}>
                    {t("entities.withholdings.month")}
                  </label>
                  <div className={s.get("withholdings.header.field.control")}>
                    <InputText
                      id="month"
                      value={withholding.month ?? ""}
                      className="flex w-full text-right"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className={s.get("withholdings.header.summary.rows.row")}>
                <div className={s.get("withholdings.header.field")}>
                  <label className={s.get("withholdings.header.field.label")}>
                    {t(
                      "entities.withholdings.withholdingsLinesCollectionLenght"
                    )}
                  </label>
                  <div className={s.get("withholdings.header.field.control")}>
                    <InputText
                      id="withholdignsLines"
                      value={withholding.summary?.installmentsNumber ?? ""}
                      className="flex w-full text-right"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <Divider />
              <div className={s.get("withholdings.header.summary.rows.row")}>
                <div className={s.get("withholdings.header.field")}>
                  <label className={s.get("withholdings.header.field.label")}>
                    {t("entities.withholdings.capitalAmount")}
                  </label>
                  <div className={s.get("withholdings.header.field.control")}>
                    <InputText
                      id="capitalAmount"
                      value={helperFormats.formatDecimal(
                        withholding.summary?.capitalAmount ?? ""
                      )}
                      className="flex w-full text-right"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className={s.get("withholdings.header.summary.rows.row")}>
                <div className={s.get("withholdings.header.field")}>
                  <label className={s.get("withholdings.header.field.label")}>
                    {t("entities.withholdings.interestsAmount")}
                  </label>
                  <div className={s.get("withholdings.header.field.control")}>
                    <InputText
                      id="interestsAmount"
                      value={helperFormats.formatDecimal(
                        withholding.summary?.interestsAmount ?? ""
                      )}
                      className="flex w-full text-right"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className={s.get("withholdings.header.summary.rows.row")}>
                <div className={s.get("withholdings.header.field")}>
                  <label className={s.get("withholdings.header.field.label")}>
                    {t("entities.withholdings.arrearsAmount")}
                  </label>
                  <div className={s.get("withholdings.header.field.control")}>
                    <InputText
                      id="arrearsAmount"
                      value={helperFormats.formatDecimal(
                        withholding.summary?.arrearsAmount ?? ""
                      )}
                      className="flex w-full text-right"
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className={s.get("withholdings.header.summary.rows.row")}>
                <div className={s.get("withholdings.header.field")}>
                  <label className={s.get("withholdings.header.field.label")}>
                    {t("entities.withholdings.interestsTaxesAmount")}
                  </label>
                  <div className={s.get("withholdings.header.field.control")}>
                    <InputText
                      id="interestsTaxesAmount"
                      value={helperFormats.formatDecimal(
                        withholding.summary?.interestsTaxesAmount ?? ""
                      )}
                      className="flex w-full text-right"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className={s.get("withholdings.header.summary.rows.row")}>
                <div className={s.get("withholdings.header.field")}>
                  <label className={s.get("withholdings.header.field.label")}>
                    {t("entities.withholdings.total")}
                  </label>
                  <div className={s.get("withholdings.header.field.control")}>
                    <InputText
                      id="total"
                      value={helperFormats.formatDecimal(
                        withholding.summary?.amountGenerated
                      )}
                      className="flex w-full text-right"
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={s.get("withholdings.header.actions")}>
            <div className={s.get("withholdings.header.actions.action")}>
              <div
                className={s.get("withholdings.header.actions.action.title")}
              >
                {t("entities.withholdings.send")}
              </div>
              <div className={s.get("withholdings.header.actions.action.rows")}>
                <div
                  className={s.get(
                    "withholdings.header.actions.action.rows.row"
                  )}
                >
                  <div
                    className={s.get(
                      "withholdings.header.actions.action.rows.row.column"
                    )}
                  >
                    <div className={s.get("withholdings.header.field")}>
                      <label
                        className={s.get("withholdings.header.field.label")}
                      >
                        {t("entities.withholdings.sentDate")}
                      </label>
                      <div
                        className={s.get("withholdings.header.field.control")}
                      >
                        <InputText
                          id="sentDate"
                          value={
                            helperFormats.isDate(withholding.sentDate)
                              ? helperFormats.formatDate(
                                new Date(withholding.sentDate)
                              )
                              : "No enviada"
                          }
                          className="flex w-full text-center"
                          readOnly
                        />
                      </div>
                    </div>
                    <div className={s.get("withholdings.header.field")}>
                      <label
                        className={s.get("withholdings.header.field.label")}
                      >
                        {t("entities.withholdings.total")}
                      </label>
                      <div
                        className={s.get("withholdings.header.field.control")}
                      >
                        <InputText
                          id="total"
                          value={helperFormats.formatDecimal(
                            withholding.summary?.amountSent
                          )}
                          className="flex w-full text-right"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={s.get(
                      "withholdings.header.actions.action.rows.row.column"
                    )}
                  >
                    <div className={s.get("withholdings.header.field")}>
                      <Button
                        id="sendButton"
                        label={
                          isLoading &&
                            withholdingState == WITHHOLDING_STATE_CREATED
                            ? `${t("buttons.updating")}...`
                            : t("entities.withholdings.send")
                        }
                        className={s.get("withholdings.header.button")}
                        onClick={(event) => {
                          event.preventDefault();
                          updateWithholdings("send", withholding.id);
                        }}
                        disabled={
                          isLoading ||
                          withholdingState != WITHHOLDING_STATE_CREATED
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={s.get("withholdings.header.actions.action")}>
              <div
                className={s.get("withholdings.header.actions.action.title")}
              >
                {t("entities.withholdings.confirm")}
              </div>
              <div className={s.get("withholdings.header.actions.action.rows")}>
                <div
                  className={s.get(
                    "withholdings.header.actions.action.rows.row"
                  )}
                >
                  <div
                    className={s.get(
                      "withholdings.header.actions.action.rows.row.column"
                    )}
                  >
                    <div className={s.get("withholdings.header.field")}>
                      <label
                        className={s.get("withholdings.header.field.label")}
                      >
                        {t("entities.withholdings.confirmedDate")}
                      </label>
                      <div
                        className={s.get("withholdings.header.field.control")}
                      >
                        <InputText
                          id="confirmedDate"
                          value={
                            helperFormats.isDate(withholding.confirmedDate)
                              ? helperFormats.formatDate(
                                new Date(withholding.confirmedDate)
                              )
                              : "No confirmada"
                          }
                          readOnly
                          className="flex w-full text-center"
                        />
                      </div>
                    </div>
                    <div className={s.get("withholdings.header.field")}>
                      <label
                        className={s.get("withholdings.header.field.label")}
                      >
                        {t("entities.withholdings.total")}
                      </label>
                      <div
                        className={s.get("withholdings.header.field.control")}
                      >
                        <InputText
                          id="total"
                          value={helperFormats.formatDecimal(
                            withholding.summary?.amountConfirmed
                          )}
                          className="flex w-full text-right"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={s.get(
                      "withholdings.header.actions.action.rows.row.column"
                    )}
                  >
                    <div className={s.get("withholdings.header.field")}>
                      <Button
                        id="confirmButton"
                        label={
                          isConfirming && currentPhase === PHASE_COLLECTING
                            ? `${t("entities.withholdings.confirming")} ${confirmProgress?.confirmedLines ?? 0}/${confirmProgress?.totalLines ?? 0}`
                            : pendingPaymentCount > 0 &&
                              pendingPaymentCount < (confirmProgress?.totalLines ?? 0)
                              ? t("entities.withholdings.confirmRetry")
                              : t("entities.withholdings.confirm")
                        }
                        className={s.get("withholdings.header.button")}
                        onClick={(event) => {
                          event.preventDefault();
                          confirmWithholding();
                        }}
                        disabled={isLoading || isConfirming || !canRunConfirmation}
                      />
                      {showConfirmationSummary && (
                        <small className="flex justify-content-center mt-1">
                          {t("entities.withholdings.confirmedLinesProgress", {
                            confirmed: confirmProgress.confirmedLines,
                            total: confirmProgress.totalLines,
                          })}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={s.get("withholdings.header.actions.action")}>
              <div
                className={s.get("withholdings.header.actions.action.title")}
              >
                {t("entities.withholdings.bill")}
              </div>
              <div className={s.get("withholdings.header.actions.action.rows")}>
                <div
                  className={s.get(
                    "withholdings.header.actions.action.rows.row"
                  )}
                >
                  <div
                    className={s.get(
                      "withholdings.header.actions.action.rows.row.column"
                    )}
                  >
                    <div className={s.get("withholdings.header.field")}>
                      <label
                        className={s.get("withholdings.header.field.label")}
                      >
                        {t("entities.withholdings.billingDate")}
                      </label>
                      <div
                        className={s.get("withholdings.header.field.control")}
                      >
                        <InputText
                          id="billingDate"
                          value={
                            helperFormats.isDate(confirmProgress?.billingDate)
                              ? helperFormats.formatDate(
                                new Date(confirmProgress.billingDate)
                              )
                              : t("entities.withholdings.notBilled")
                          }
                          readOnly
                          className="flex w-full text-center"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={s.get(
                      "withholdings.header.actions.action.rows.row.column"
                    )}
                  >
                    <div className={s.get("withholdings.header.field")}>
                      <Button
                        id="billButton"
                        label={
                          isConfirming && currentPhase === PHASE_BILLING
                            ? `${t("entities.withholdings.billing")} ${confirmProgress?.billedLines ?? 0}/${confirmProgress?.totalLines ?? 0}`
                            : t("entities.withholdings.bill")
                        }
                        className={s.get("withholdings.header.button")}
                        onClick={(event) => {
                          event.preventDefault();
                          setShowBillingDialog(true);
                        }}
                        disabled={isLoading || isConfirming || !canBill}
                      />
                      {showConfirmationSummary && (
                        <React.Fragment>
                          <small className="flex justify-content-center mt-1">
                            {t("entities.withholdings.billedLinesProgress", {
                              billed: confirmProgress.billedLines,
                              total: confirmProgress.totalLines,
                            })}
                          </small>
                          {pendingBillingCount > 0 &&
                            withholdingState === WITHHOLDING_STATE_CONFIRMED && (
                              // El cobro ya está firme: lo que falta son comprobantes. Se dice acá
                              // para que no haya que deducirlo de la grilla.
                              <small
                                className="flex justify-content-center mt-1"
                                style={{ color: "#a16207" }}
                              >
                                {t("entities.withholdings.pendingBillingNotice", {
                                  count: pendingBillingCount,
                                })}
                              </small>
                            )}
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={s.get("withholdings.header.actions.action")}>
              <div
                className={s.get("withholdings.header.actions.action.title")}
              >
                {t("entities.withholdings.conciliate")}
              </div>
              <div className={s.get("withholdings.header.actions.action.rows")}>
                <div
                  className={s.get(
                    "withholdings.header.actions.action.rows.row"
                  )}
                >
                  <div
                    className={s.get(
                      "withholdings.header.actions.action.rows.row.column"
                    )}
                  >
                    <div className={s.get("withholdings.header.field")}>
                      <label
                        className={s.get("withholdings.header.field.label")}
                      >
                        {t("entities.withholdings.conciliationDate")}
                      </label>
                      <div
                        className={s.get("withholdings.header.field.control")}
                      >
                        <InputText
                          id="conciliationDate"
                          value={
                            helperFormats.isDate(withholding.conciliationDate)
                              ? helperFormats.formatDate(
                                new Date(withholding.conciliationDate)
                              )
                              : "No conciliada"
                          }
                          className="flex w-full text-center"
                          readOnly
                        />
                      </div>
                    </div>
                    <div className={s.get("withholdings.header.field")}>
                      <label
                        className={s.get("withholdings.header.field.label")}
                      >
                        {t("entities.withholdings.total")}
                      </label>
                      <div
                        className={s.get("withholdings.header.field.control")}
                      >
                        <InputText
                          id="total"
                          value={helperFormats.formatDecimal(
                            withholding.summary?.amountConcilied
                          )}
                          className="flex w-full text-right"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={s.get(
                      "withholdings.header.actions.action.rows.row.column"
                    )}
                  >
                    <div className={s.get("withholdings.header.field")}>
                      <Button
                        id="conciliationButton"
                        label={
                          isLoading &&
                            withholdingState == WITHHOLDING_STATE_CONFIRMED
                            ? `${t("buttons.updating")}...`
                            : t("entities.withholdings.conciliate")
                        }
                        className={s.get("withholdings.header.button")}
                        onClick={(event) => {
                          event.preventDefault();
                          updateWithholdings("conciliate", withholding.id);
                        }}
                        disabled={isLoading || isConfirming || !canConciliate}
                      />
                      {withholdingState === WITHHOLDING_STATE_CONFIRMED &&
                        !billingComplete && (
                          // Sin esto, el botón apagado no dice por qué: se lee como que la
                          // retención no está lista, cuando lo que falta es facturar.
                          <small
                            className="flex justify-content-center mt-1"
                            style={{ color: "#a16207" }}
                          >
                            {t("entities.withholdings.conciliateNeedsBilling")}
                          </small>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <WithholdingsLines
          settings={{
            ...settings,
            currentWithholdingState: withholdingState,
            withholdingYear: withholding.year,
            withholdingMonth: withholding.month,
            withholdingCreatedDate: withholding.createdDate,
            withholdingsLines: withholding.withholdingsLinesCollection,
            // Sin fecha fijada no se puede facturar una línea suelta: la fecha la fija la corrida
            // masiva, y todos los comprobantes de la retención tienen que llevar la misma.
            withholdingBillingDate: confirmProgress?.billingDate,
            fetchData: loadWithholding,
            linesRefreshToken: linesRefreshToken,
          }}
        />

        <div className="flex justify-content-between mt-6 mb-2">
          <Button
            label={t("buttons.cancel")}
            icon="pi pi-times"
            className="p-button-text btn-secondary-crud"
            onClick={() => settings.hideDialog("CANCEL")}
            type="button"
            disabled={isLoading || isConfirming}
          />
        </div>

        {/* Avance de la confirmación. Va al medio de la pantalla porque el proceso puede durar
            minutos: hay que poder ver de un vistazo por dónde va sin buscar en la grilla. */}
        <Dialog
          visible={isConfirming || confirmFailure != null || confirmFailures.length > 0}
          header={t(
            currentPhase === PHASE_BILLING
              ? "entities.withholdings.billProgressTitle"
              : "entities.withholdings.confirmProgressTitle",
            {
              year: withholding.year,
              month: withholding.month,
            }
          )}
          modal
          closable={!isConfirming}
          onHide={closeConfirmDialog}
          style={{ width: "36rem" }}
        >
          <div className="flex flex-column" style={{ gap: "1rem" }}>
            {/* Una sola barra: la del proceso que se está corriendo. Cobrar y facturar son dos
                procesos distintos, cada uno con su botón, así que nunca avanzan a la vez. */}
            {currentPhase === PHASE_BILLING ? (
              <div className="flex flex-column" style={{ gap: "6px" }}>
                <div className="flex align-items-center justify-content-between">
                  <span style={{ fontWeight: 600 }}>
                    <i className="pi pi-file mr-2" style={{ color: "#16a34a" }} />
                    {t("entities.withholdings.phaseBilling")}
                  </span>
                  <small>
                    {t("entities.withholdings.confirmProgressBilled", {
                      count: confirmProgress?.billedLines ?? 0,
                    })}
                    {" de "}
                    {confirmProgress?.totalLines ?? 0}
                  </small>
                </div>
                <ProgressBar
                  value={billingPercentage}
                  displayValueTemplate={() =>
                    `${confirmProgress?.billedLines ?? 0} / ${confirmProgress?.totalLines ?? 0}`
                  }
                  color="#16a34a"
                  style={{ height: "1.5rem" }}
                />
              </div>
            ) : (
              <div className="flex flex-column" style={{ gap: "6px" }}>
                <div className="flex align-items-center justify-content-between">
                  <span style={{ fontWeight: 600 }}>
                    <i className="pi pi-wallet mr-2" style={{ color: "#2563eb" }} />
                    {t("entities.withholdings.phaseCollecting")}
                  </span>
                  <small>
                    {t("entities.withholdings.confirmProgressPaid", {
                      count: confirmProgress?.confirmedLines ?? 0,
                    })}
                    {" de "}
                    {confirmProgress?.totalLines ?? 0}
                  </small>
                </div>
                <ProgressBar
                  value={collectingPercentage}
                  displayValueTemplate={() =>
                    `${confirmProgress?.confirmedLines ?? 0} / ${confirmProgress?.totalLines ?? 0}`
                  }
                  style={{ height: "1.5rem" }}
                />
              </div>
            )}

            {isConfirming ? (
              <div className="flex align-items-center" style={{ gap: "8px" }}>
                <i className="pi pi-spin pi-spinner" />
                <span>
                  {!currentLineName
                    ? t("entities.withholdings.confirming")
                    : currentPhase === PHASE_BILLING
                      ? t("entities.withholdings.confirmProgressBilling", {
                        beneficiary: currentLineName,
                      })
                      : t("entities.withholdings.confirmProgressCollecting", {
                        beneficiary: currentLineName,
                      })}
                  {phaseTotal > 0 ? ` (${phaseDone + 1} de ${phaseTotal})` : ""}
                </span>
              </div>
            ) : null}

            {/* Resumen de lo que no se pudo hacer. Si lo que falló fue la facturación, el cobro ya
                está firme y esto no bloquea a nadie: son comprobantes por emitir. */}
            {!isConfirming && confirmFailures.length > 0 ? (
              <React.Fragment>
                <Divider />
                <div className="flex flex-column" style={{ gap: "6px" }}>
                  <span style={{ fontWeight: 600, color: "#a16207" }}>
                    <i className="pi pi-exclamation-triangle mr-2" />
                    {t(
                      currentPhase === PHASE_BILLING
                        ? "entities.withholdings.billSummaryTitle"
                        : "entities.withholdings.confirmSummaryTitle",
                      { count: confirmFailures.length }
                    )}
                  </span>
                  <small>
                    {t(
                      currentPhase === PHASE_BILLING
                        ? "entities.withholdings.billSummaryHelp"
                        : "entities.withholdings.confirmSummaryHelp"
                    )}
                  </small>
                  <div
                    className="flex flex-column mt-2"
                    style={{ gap: "4px", maxHeight: "12rem", overflowY: "auto" }}
                  >
                    {confirmFailures.map((fallo) => (
                      <small key={fallo.lineId}>
                        <b>{fallo.beneficiaryName}</b>
                        {": "}
                        {fallo.reason}
                        {fallo.billingStatus === "TO_VERIFY"
                          ? ` — ${t("entities.withholdings.withholdingsLines.status.toVerify")}`
                          : ""}
                      </small>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            ) : null}

            {confirmFailure ? (
              <React.Fragment>
                <Divider />
                <div className="flex flex-column" style={{ gap: "6px" }}>
                  <span style={{ color: "#dc2626", fontWeight: 600 }}>
                    <i className="pi pi-times-circle mr-2" />
                    {t("entities.withholdings.confirmProgressStopped", {
                      beneficiary: confirmFailure.beneficiaryName,
                    })}
                  </span>
                  <small>{confirmFailure.reason}</small>
                  <small>
                    {t("entities.withholdings.confirmProgressStoppedHelp")}
                  </small>
                </div>
              </React.Fragment>
            ) : null}

            {!isConfirming ? (
              <div className="flex justify-content-end">
                <Button
                  label={t("buttons.close")}
                  icon="pi pi-times"
                  className="p-button-text btn-secondary-crud"
                  onClick={closeConfirmDialog}
                  type="button"
                />
              </div>
            ) : null}
          </div>
        </Dialog>

        {/* La fecha de los comprobantes se pide antes de empezar: es lo único que hay que decidir,
            y una vez emitido el primero ya no se puede cambiar. */}
        <Dialog
          visible={showBillingDialog}
          header={t("entities.withholdings.billingDialogTitle", {
            year: withholding.year,
            month: withholding.month,
          })}
          modal
          className="flex w-4"
          onHide={() => setShowBillingDialog(false)}
        >
          <WithholdingsBillingDate
            settings={{
              billingDate: confirmProgress?.billingDate,
              billingDateFrozen: confirmProgress?.billingDateFrozen ?? false,
              pendingBillingCount,
              bill: billWithholding,
              hideDialog: () => setShowBillingDialog(false),
            }}
          />
        </Dialog>
      </div>
    </React.Fragment>
  );
};

export default WithholdingsForm;

